import React, { useEffect, useMemo, useRef, useState } from "react";
import { List } from "@refinedev/antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Form, Modal, Popconfirm, Space, Table, Tag, Typography, message } from "antd";
import Search from "antd/es/input/Search";
import { Link, useNavigate } from "react-router-dom";
import { apiErrorMessage, eventTypeLabel, formatDateTime, subscriptionStatus, toSubscriptionRequest } from "../labels";
import { FALLBACK_EVENT_TYPES, WebhookFormValues, WebhookSubscription } from "../types";
import { useWebhookApi, webhookKeys } from "../useWebhookApi";
import { WebhookSubscriptionFields } from "./WebhookSubscriptionFields";

export const WebhookListView: React.FC = () => {
  const api = useWebhookApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [form] = Form.useForm<WebhookFormValues>();

  const subscriptionsQuery = useQuery<WebhookSubscription[]>({
    queryKey: webhookKeys.list,
    queryFn: async () => {
      const result = await api.listSubscriptions();
      return Array.isArray(result) ? result : [];
    },
  });

  const eventTypesQuery = useQuery({
    queryKey: webhookKeys.eventTypes,
    queryFn: async () => {
      const result = await api.listEventTypes();
      return Array.isArray(result) && result.length > 0 ? result : FALLBACK_EVENT_TYPES;
    },
  });

  const eventTypes = eventTypesQuery.data ?? FALLBACK_EVENT_TYPES;
  const eventTypesRef = useRef(eventTypes);
  eventTypesRef.current = eventTypes;

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteSubscription(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: webhookKeys.list });
    },
  });

  useEffect(() => {
    if (!createOpen) {
      return;
    }
    form.setFieldsValue({
      name: "",
      url: "",
      apiKeyHeaderName: "X-Webhook-Key",
      eventTypes: eventTypesRef.current,
    });
    setCreateError(null);
  }, [createOpen, form]);

  const createMutation = useMutation({
    mutationFn: (values: WebhookFormValues) => api.createSubscription(toSubscriptionRequest(values)),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: webhookKeys.list });
      setCreateOpen(false);
      message.success("Webhooken skapades");
      navigate(`/webhooks/${created.id}?created=1`);
    },
    onError: (error) => {
      setCreateError(apiErrorMessage(error, "Kunde inte skapa webhooken"));
    },
  });

  const filtered = useMemo(() => {
    const rows = subscriptionsQuery.data ?? [];
    const term = search.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    return rows.filter((row) => row.name.toLowerCase().includes(term) || row.url.toLowerCase().includes(term));
  }, [subscriptionsQuery.data, search]);

  const autoDisabled = (subscriptionsQuery.data ?? []).filter(
    (row) => !row.isActive && row.disabledReason !== "Disabled manually."
  );

  const handleDelete = async (id: number) => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success("Webhooken raderades");
    } catch (error) {
      message.error(apiErrorMessage(error, "Kunde inte radera webhooken"));
    }
  };

  return (
    <Card bordered={false} className="criclebox h-full">
      <List
        title="Webhooks"
        headerButtons={
          <Space>
            <Search
              placeholder="Sök namn eller URL..."
              allowClear
              onSearch={setSearch}
              onChange={(event) => setSearch(event.target.value)}
              style={{ width: 280 }}
            />
            <Button type="primary" onClick={() => setCreateOpen(true)}>
              Ny webhook
            </Button>
          </Space>
        }
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          En webhook tar emot produktändringar på en adress ni anger. Händelsen säger vilket artikelnummer som ändrats,
          och mottagaren hämtar aktuell data från export-API:t. Leverans sker inom ett par minuter.
        </Typography.Paragraph>

        {autoDisabled.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="En eller flera webhooks har stängts av efter upprepade fel"
            description={
              <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                {autoDisabled.map((row) => (
                  <li key={row.id}>
                    <Link to={`/webhooks/${row.id}`}>{row.name}</Link>
                    {row.disabledReason ? `: ${row.disabledReason}` : ""}
                  </li>
                ))}
              </ul>
            }
          />
        )}

        <Table<WebhookSubscription>
          rowKey="id"
          dataSource={filtered}
          loading={subscriptionsQuery.isLoading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            hideOnSinglePage: true,
          }}
          locale={{ emptyText: subscriptionsQuery.isError ? "Kunde inte hämta webhooks" : "Inga webhooks ännu" }}
        >
          <Table.Column<WebhookSubscription>
            title="Namn"
            dataIndex="name"
            render={(_, record) => <Link to={`/webhooks/${record.id}`}>{record.name}</Link>}
          />
          <Table.Column<WebhookSubscription>
            title="URL"
            dataIndex="url"
            render={(url: string) => (
              <Typography.Text ellipsis={{ tooltip: url }} style={{ maxWidth: 280 }}>
                {url}
              </Typography.Text>
            )}
          />
          <Table.Column<WebhookSubscription>
            title="Händelser"
            dataIndex="eventTypes"
            render={(types: string[]) => (
              <Space size={4} wrap>
                {(types ?? []).map((type) => (
                  <Tag key={type}>{eventTypeLabel(type)}</Tag>
                ))}
              </Space>
            )}
          />
          <Table.Column<WebhookSubscription>
            title="Status"
            render={(_, record) => {
              const status = subscriptionStatus(record);
              return <Tag color={status.color}>{status.label}</Tag>;
            }}
          />
          <Table.Column<WebhookSubscription> title="Väntar" dataIndex="pendingDeliveryCount" width={90} />
          <Table.Column<WebhookSubscription> title="Misslyckade" dataIndex="failedDeliveryCount" width={120} />
          <Table.Column<WebhookSubscription>
            title="Senast levererad"
            dataIndex="lastDeliveredAt"
            render={(value: string | null) => formatDateTime(value)}
          />
          <Table.Column<WebhookSubscription>
            title="Handling"
            width={180}
            render={(_, record) => (
              <Space>
                <Button size="small" onClick={() => navigate(`/webhooks/${record.id}`)}>
                  Öppna
                </Button>
                <Popconfirm
                  title="Radera webhook?"
                  description="Webhooken och dess leveranslogg raderas. Det går inte att ångra."
                  okText="Radera"
                  cancelText="Avbryt"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => handleDelete(record.id)}
                >
                  <Button
                    danger
                    size="small"
                    loading={deleteMutation.isLoading && deleteMutation.variables === record.id}
                  >
                    Radera
                  </Button>
                </Popconfirm>
              </Space>
            )}
          />
        </Table>
      </List>

      <Modal
        title="Ny webhook"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        okText="Skapa"
        cancelText="Avbryt"
        confirmLoading={createMutation.isLoading}
        onOk={() => form.submit()}
        destroyOnClose
        width={560}
      >
        <Typography.Paragraph type="secondary">
          API-nyckeln skapas när webhooken sparas och visas på nästa sida.
        </Typography.Paragraph>
        {createError && (
          <Alert type="error" showIcon message={createError} style={{ marginBottom: 16 }} />
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            setCreateError(null);
            createMutation.mutate(values);
          }}
        >
          <WebhookSubscriptionFields eventTypes={eventTypes} eventTypesLoading={eventTypesQuery.isLoading} />
        </Form>
      </Modal>
    </Card>
  );
};
